import i18n from 'utils/i18n';

/**
 * PLA-8815 / PLA-8828: the one rule for turning a GOVERNED CODE off the wire into copy on the
 * screen, shared by every group that has one and by both layers that draw one —
 * `confidence_limiting_reason` on Customer 360's metric confidence badge and on Customer Insights'
 * card confidence pill, `reason_code` and `calculation_status` under a card's data state.
 *
 * Three different vocabularies, one discipline: a code becomes a key, the key is rendered only if it
 * resolves, and a code with no copy yet renders NOTHING. A raw identifier
 * (`governed_non_scalar_grain`) on a customer-facing card is worse than a missing line — it reads as
 * a defect, and it is the pipeline's private language.
 *
 * Only a code-shaped token is keyed, so a supplier that sends a sentence has it recognised as prose
 * by the caller rather than reflected back as a lookup miss.
 */
const REASON_CODE_PATTERN = /^[a-z0-9][a-z0-9_-]*$/i;

export const isReasonCode = (value: string): boolean =>
  REASON_CODE_PATTERN.test(typeof value === 'string' ? value.trim() : '');

/**
 * The translation key for `code` under `group`, or `null` when the code is not code-shaped or the
 * group carries no copy for it. `group` is a full dotted path including the module prefix.
 */
export const reasonCopyKey = (group: string, code: string | null | undefined): string | null => {
  const trimmed = typeof code === 'string' ? code.trim() : '';

  if (!isReasonCode(trimmed)) {
    return null;
  }

  const key = `${group}.${trimmed
    .split(/[_-]+/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('')}`;

  return i18n.exists(key) ? key : null;
};
